/* Cart page — Paolina Roma (Dawn port of Motion sections/main-cart.liquid)
 *
 * Three things live here and nothing else:
 *   <pr-cart-items>    subclass of Dawn's CartItems. Same engine (updateQuantity,
 *                      validateQuantity, enableLoading, live regions, errors,
 *                      CartPerformance, pubsub) with a different set of sections
 *                      to re-render, because the whole cart page is ONE section
 *                      instead of Dawn's items + footer pair.
 *   <pr-shipping-bar>  free-shipping progress. The zone (italy/europe/world) is
 *                      already resolved server-side in Liquid, so this only
 *                      converts the threshold into the presentment currency and
 *                      fills the bar.
 *   delegated clicks   remove line (with the Motion confirm) and upsell add.
 *
 * Requires assets/cart.js to have run first: with cart_type = 'drawer' it is
 * loaded by snippets/cart-drawer.liquid, which theme.liquid renders before
 * <main>, so document order (and therefore defer order) guarantees it. The
 * section also loads it itself when the drawer is off. If both ever move, the
 * guard at the bottom says so out loud instead of failing silently.
 *
 * Money is formatted by a module-scoped port of Motion's formatWithDelimiters.
 * Motion assigned it to window.Shopify.formatMoney; overwriting a global from a
 * section is not done here.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ money */

  var PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = typeof precision === 'number' ? precision : 2;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number === null) return '0';

    var value = (number / 100.0).toFixed(precision);
    var parts = value.split('.');
    var units = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';

    return units + cents;
  }

  /* shop.money_format can carry markup and HTML entities (&euro;). It travels in
     a data attribute, so the browser decodes it once; tags and any remaining
     entity are cleaned here, otherwise the message would read "&euro;12,00". */
  function cleanFormat(raw) {
    if (!raw) return '';
    var stripped = String(raw).replace(/<[^>]*>/g, '');
    var decoder = document.createElement('textarea');
    decoder.innerHTML = stripped;
    return decoder.value;
  }

  function formatMoney(cents, format) {
    if (!format) return '';
    var match = format.match(PLACEHOLDER);
    if (!match) return '';

    var value;
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        return '';
    }

    return format.replace(PLACEHOLDER, value);
  }

  /* --------------------------------------------------------- currency rate */

  function cartRoute() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  /* Thresholds are stored in euros; cart.total_price arrives in the presentment
     currency. One lookup per page, cached at module level. */
  var ratePromise = null;

  function getCurrencyRate() {
    if (ratePromise) return ratePromise;

    var injected = window.Shopify && window.Shopify.currency && window.Shopify.currency.rate;
    var parsed = parseFloat(injected);

    if (parsed > 0) {
      ratePromise = Promise.resolve(parsed);
      return ratePromise;
    }

    ratePromise = fetch(cartRoute() + '.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        return parseFloat(data.currency_rate) || 1;
      })
      .catch(function () {
        return 1;
      });

    return ratePromise;
  }

  /* ------------------------------------------------------ <pr-shipping-bar> */

  class PrShippingBar extends HTMLElement {
    connectedCallback() {
      var fill = this.querySelector('.pr-shipping-progress__fill');
      var message = this.querySelector('.pr-shipping-progress__message');
      var total = parseInt(this.dataset.total, 10) || 0;
      var thresholdUnits = parseFloat(this.dataset.threshold) || 0;
      var incomplete = this.dataset.msgIncomplete || '';
      var complete = this.dataset.msgComplete || '';
      var format = cleanFormat(this.dataset.moneyFormat);

      if (thresholdUnits <= 0) {
        if (fill) fill.style.width = '100%';
        if (message) message.textContent = complete;
        return;
      }

      getCurrencyRate().then(function (rate) {
        var threshold = thresholdUnits * 100 * rate;
        var remaining = threshold - total;
        var progress = Math.min((total / threshold) * 100, 100);

        /* The 100ms delay is Motion's: it lets the 0% start frame paint so the
           width transition is visible on load. */
        if (fill) {
          window.setTimeout(function () {
            fill.style.width = progress + '%';
          }, 100);
        }

        if (!message) return;

        if (remaining <= 0) {
          message.textContent = complete;
          return;
        }

        var amount = formatMoney(Math.round(remaining), format);
        message.textContent = amount
          ? incomplete.replace('[amount]', amount)
          : /* No usable money format: show the zone message without a figure
               rather than a wrong one. */
            incomplete.replace('[amount]', '').replace(/\s{2,}/g, ' ').trim();
      });
    }
  }

  if (!customElements.get('pr-shipping-bar')) {
    customElements.define('pr-shipping-bar', PrShippingBar);
  }

  /* --------------------------------------------------------- <pr-cart-items> */

  if (typeof CartItems === 'undefined') {
    console.error(
      '[paolina-cart] assets/cart.js did not run before section-main-cart.js: ' +
        'the cart page cannot update quantities. Check the script order in ' +
        'layout/theme.liquid (cart-drawer is rendered before <main>).'
    );
    return;
  }

  class PrCartItems extends CartItems {
    constructor() {
      super();
      this.settleTimer = null;
      this.addEventListener('click', this.onPrClick.bind(this));
    }

    connectedCallback() {
      super.connectedCallback();

      /* First-paint animations run once, then the class freezes them so an AJAX
         re-render does not replay the fade-in of every line. */
      if (!this.settleTimer && !this.classList.contains('prc--settled')) {
        var host = this;
        this.settleTimer = window.setTimeout(function () {
          host.classList.add('prc--settled');
        }, 800);
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.settleTimer) {
        window.clearTimeout(this.settleTimer);
        this.settleTimer = null;
      }
    }

    /* The order note and the Charm Studio "copies" input live inside this
       element too (Dawn keeps them in a separate section). Without this guard
       their change event would reach CartItems.validateQuantity, which would
       read an undefined line index and throw. */
    onChange(event) {
      var target = event.target;
      if (!target || typeof target.matches !== 'function') return;
      if (!target.matches('input[name="updates[]"]')) return;
      super.onChange(event);
    }

    /* One section, not Dawn's two. main-cart-footer is deliberately absent:
       asking for it would hand the summary the same HTML as the line list.
       The drawer is added only when it is actually on the page: with
       cart_type != 'drawer' theme.liquid does not render #CartDrawer, and
       cart.js does document.getElementById(section.id).querySelector(...)
       with no null guard, so a missing element would kill the whole re-render.
       Without this entry the drawer, which theme.liquid renders on EVERY
       template, keeps the pre-update markup while the customer edits the cart
       page: stale totals, and stale data-index values that make the next click
       inside the drawer address the wrong cart line. */
    getSectionsToRender() {
      var sections = [
        {
          id: 'main-cart-items',
          section: this.dataset.id,
          selector: '.js-contents',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
        {
          id: 'cart-live-region-text',
          section: 'cart-live-region-text',
          selector: '.shopify-section',
        },
      ];

      if (document.getElementById('CartDrawer')) {
        sections.push({
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        });
      }

      return sections;
    }

    /* Reached when something else (the drawer, the Charm Studio card) publishes
       a cart update. Dawn's version hardcodes ?section_id=main-cart-items. */
    onCartUpdate() {
      var host = this;
      return fetch(cartRoute() + '?section_id=' + this.dataset.id)
        .then(function (response) {
          return response.text();
        })
        .then(function (responseText) {
          var html = new DOMParser().parseFromString(responseText, 'text/html');
          host.renderContents(html.querySelector('.js-contents'));
        })
        .catch(function (error) {
          console.error(error);
        });
    }

    renderContents(source) {
      var target = this.querySelector('.js-contents');
      if (target && source) target.innerHTML = source.innerHTML;
    }

    renderBubble(sectionHtml) {
      if (!sectionHtml) return;
      var bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      var source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }

    /* The drawer lives on every template, so it has to follow what happens on
       the cart page. The quantity path gets this for free from
       getSectionsToRender(); the upsell add does its own fetch, hence this. */
    renderDrawer(sectionHtml) {
      if (!sectionHtml) return;
      var drawer = document.getElementById('CartDrawer');
      var target = drawer && drawer.querySelector('.drawer__inner');
      if (!target) return;
      var source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.drawer__inner');
      if (source) target.innerHTML = source.innerHTML;
    }

    onPrClick(event) {
      var removeButton = event.target.closest('[data-pr-remove]');
      if (removeButton) {
        event.preventDefault();
        this.removeLine(removeButton, event);
        return;
      }

      var upsellButton = event.target.closest('[data-pr-upsell-add]');
      if (upsellButton) {
        event.preventDefault();
        this.addUpsell(upsellButton);
      }
    }

    /* Motion asks for confirmation before emptying a line; kept for parity.
       <cart-remove-button> is not usable here: it does closest('cart-items'). */
    removeLine(button, event) {
      var confirmText = this.dataset.removeConfirm;
      if (confirmText && !window.confirm(confirmText)) return;
      this.updateQuantity(button.dataset.index, 0, event);
    }

    addUpsell(button) {
      if (button.disabled) return;

      var variantId = button.dataset.variantId;
      if (!variantId) return;

      var host = this;
      var errors = document.getElementById('cart-errors');
      var originalLabel = button.textContent;

      button.disabled = true;
      if (this.dataset.upsellAdding) button.textContent = this.dataset.upsellAdding;
      if (errors) errors.textContent = '';

      /* Same section list the quantity path uses, so the drawer is refreshed
         here too instead of being left with the pre-add markup. */
      var sectionIds = this.getSectionsToRender().map(function (section) {
        return section.section;
      });

      var body = JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds,
        sections_url: window.location.pathname,
      });

      fetch(window.routes.cart_add_url, Object.assign({}, fetchConfig(), { body: body }))
        .then(function (response) {
          return response.json();
        })
        .then(function (state) {
          if (state.status) throw new Error(state.description || state.message || 'cart/add failed');

          var sections = state.sections || {};
          var source = sections[host.dataset.id];

          if (source) {
            host.renderContents(new DOMParser().parseFromString(source, 'text/html').querySelector('.js-contents'));
          }
          host.renderBubble(sections['cart-icon-bubble']);
          host.renderDrawer(sections['cart-drawer']);

          /* The button was replaced by the re-render: find its twin and flag it
             as added. It resets by itself at the next render, so no timer. */
          var reborn = host.querySelector('[data-pr-upsell-add][data-variant-id="' + variantId + '"]');
          if (reborn && host.dataset.upsellAdded) reborn.textContent = host.dataset.upsellAdded;

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: state,
            variantId: variantId,
          });
        })
        .catch(function (error) {
          console.error(error);
          button.disabled = false;
          button.textContent = originalLabel;
          if (errors) errors.textContent = (window.cartStrings && window.cartStrings.error) || '';
        });
    }
  }

  if (!customElements.get('pr-cart-items')) {
    customElements.define('pr-cart-items', PrCartItems);
  }

  /* Mirror image of getSectionsToRender() above: a change made INSIDE the drawer
     while the customer is standing on /cart must re-render the page list too, or
     the page keeps serving stale data-index values to the next click. Patched
     from here so assets/cart-drawer.js stays stock.

     whenDefined, not customElements.get(): theme.liquid loads cart-drawer.js at
     the very end of <body> (line 466), i.e. AFTER this file, which the cart
     section loads inside <main>. Both are defer, so document order applies and
     the class does not exist yet at this point. Patching the prototype still
     reaches instances that were already upgraded, because CartItems dispatches
     through this.getSectionsToRender(). On any other template #main-cart-items
     is absent and the drawer behaves exactly as stock. */
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer-items').then(function () {
      var DrawerItems = customElements.get('cart-drawer-items');
      if (!DrawerItems || DrawerItems.prototype.prCartPageMirrored) return;

      var baseSections = DrawerItems.prototype.getSectionsToRender;
      if (typeof baseSections !== 'function') return;

      DrawerItems.prototype.getSectionsToRender = function () {
        var sections = baseSections.call(this);
        var page = document.getElementById('main-cart-items');
        if (page && page.dataset.id) {
          sections.push({
            id: 'main-cart-items',
            section: page.dataset.id,
            selector: '.js-contents',
          });
        }
        return sections;
      };

      DrawerItems.prototype.prCartPageMirrored = true;
    });
  }
})();
